'use client';

import { useState, useMemo } from 'react';
import { useEffectOnce } from 'libs/useEffectOnce';
import pick from 'lodash-es/pick';
import omit from 'lodash-es/omit';
import { ELEMENT_TYPE, CSSVariablesMap, FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type UseIndicatorReturn } from 'shop-ui/tools/Indicator';
import { type LayoutNodeStyle } from 'app/shop-ui/types';
import { uiStyleUpdater } from 'shop-ui/tools';
import { getS3ObjectUrl } from 'libs/url';
import {
    type MotionGesture,
    type MotionEffectType,
    type MotionDuration,
    type MotionRange,
} from 'app/shop-ui/component/MotionableBox';
import {
    getVariationClassName,
    getBooleanStyleClassName,
    getIconConfig,
    getCSSVariableValueMap,
} from './utils';

export interface UsePreviewUIStylePropTypes {
    id: string;
    type: ELEMENT_TYPE;
    style: LayoutNodeStyle;
    content: string;
    feature: FEATURE_FLAG_KEYS[];
    getResizedStyleMap?: UseIndicatorReturn['actions']['getResizedStyleMap'];
    getCellResizedStyleMap?: UseIndicatorReturn['actions']['getResizedStyleMap'];
}

const dynamicSpacingStyleMap = {
    [CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_TOP]]: {
        tablet: '--shop-ui-custom-padding-top-tablet',
        mobile: '--shop-ui-custom-padding-top-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_RIGHT]]: {
        tablet: '--shop-ui-custom-padding-right-tablet',
        mobile: '--shop-ui-custom-padding-right-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_BOTTOM]]: {
        tablet: '--shop-ui-custom-padding-bottom-tablet',
        mobile: '--shop-ui-custom-padding-bottom-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_LEFT]]: {
        tablet: '--shop-ui-custom-padding-left-tablet',
        mobile: '--shop-ui-custom-padding-left-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.MARGIN_TOP]]: {
        tablet: '--shop-ui-custom-margin-top-tablet',
        mobile: '--shop-ui-custom-margin-top-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.MARGIN_RIGHT]]: {
        tablet: '--shop-ui-custom-margin-right-tablet',
        mobile: '--shop-ui-custom-margin-right-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.MARGIN_BOTTOM]]: {
        tablet: '--shop-ui-custom-margin-bottom-tablet',
        mobile: '--shop-ui-custom-margin-bottom-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.MARGIN_LEFT]]: {
        tablet: '--shop-ui-custom-margin-left-tablet',
        mobile: '--shop-ui-custom-margin-left-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.GRID_GAP]]: {
        tablet: '--shop-ui-custom-grid-gap-tablet',
        mobile: '--shop-ui-custom-grid-gap-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_X]]: {
        tablet: '--shop-ui-custom-button-padding-x-tablet',
        mobile: '--shop-ui-custom-button-padding-x-mobile',
    },
    [CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_Y]]: {
        tablet: '--shop-ui-custom-button-padding-y-tablet',
        mobile: '--shop-ui-custom-button-padding-y-mobile',
    },
};

function calculateSpacing(desktopSpacing: string) {
    let tabletSpacing = '';
    let mobileSpacing = '';

    const desktopSpacingNumber = Number(desktopSpacing.replace('px', ''));

    switch (true) {
        case desktopSpacingNumber > 40:
            tabletSpacing = `${desktopSpacingNumber * 0.5}px`;
            mobileSpacing = `${desktopSpacingNumber * 0.4}px`;

            break;
        case desktopSpacingNumber >= 24 && desktopSpacingNumber <= 40:
            tabletSpacing = `${desktopSpacingNumber * 0.8}px`;
            mobileSpacing = `${desktopSpacingNumber * 0.6}px`;

            break;
        case desktopSpacingNumber > 16 && desktopSpacingNumber < 24:
            tabletSpacing = `${desktopSpacingNumber * 0.8}px`;
            mobileSpacing = `${desktopSpacingNumber * 0.8}px`;

            break;
        default:
            tabletSpacing = desktopSpacing;
            mobileSpacing = desktopSpacing;

            break;
    }

    return { tabletSpacing, mobileSpacing };
}

function getDynamicSpacingStyleMap(
    styleMap: Record<string, string>,
    type?: ELEMENT_TYPE
) {
    const result = styleMap;

    for (const [key, value] of Object.entries(styleMap)) {
        const isStartWithZero =
            value &&
            !value.endsWith('fr') &&
            (/^(0|0\.)/.test(value) || /^0[^\d]/.test(value));

        if (isStartWithZero) {
            delete result[key];
        } else if (value && dynamicSpacingStyleMap[key]) {
            const { tabletSpacing, mobileSpacing } = calculateSpacing(value);

            result[dynamicSpacingStyleMap[key].tablet] = tabletSpacing;

            result[dynamicSpacingStyleMap[key].mobile] = mobileSpacing;

            if (type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL) {
                if (key === CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_LEFT]) {
                    result['--shop-ui-custom-primary-offset-left-mobile'] = mobileSpacing;
                }

                if (key === CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_RIGHT]) {
                    result['--shop-ui-custom-primary-offset-right-mobile'] =
                        mobileSpacing;
                }
            }
        }
    }

    return result;
}

export function usePreviewUIStyle(props: UsePreviewUIStylePropTypes) {
    const {
        id,
        type,
        style: styleMap,
        content: originContent,
        feature: selfFeature,
        getResizedStyleMap,
        getCellResizedStyleMap,
    } = props;

    const [selfStyleMap, setSelfStyleMap] = useState(() => {
        const resizeHandleStyleList = [
            FEATURE_FLAG_KEYS.PADDING_LEFT,
            FEATURE_FLAG_KEYS.PADDING_RIGHT,
            FEATURE_FLAG_KEYS.PADDING_TOP,
            FEATURE_FLAG_KEYS.PADDING_BOTTOM,
        ];

        const result = styleMap;

        for (const key of resizeHandleStyleList) {
            if (!result[key]) {
                result[key] = '0';
            }
        }

        return result;
    });

    const [selfContent, setSelfContent] = useState(originContent);

    function enhanceSetStyleMap(newStyleMap: typeof selfStyleMap) {
        setSelfStyleMap((prev) => {
            if (typeof prev !== 'object' || typeof newStyleMap !== 'object') {
                return newStyleMap;
            }

            return {
                ...prev,
                ...newStyleMap,
            };
        });
    }

    const property = useMemo(() => {
        const style = getCSSVariableValueMap(selfStyleMap);

        const { variation, className } = getVariationClassName(type, selfStyleMap);

        let _className = className;

        if (type === ELEMENT_TYPE.RICH_TEXT || type === ELEMENT_TYPE.BUTTON) {
            const booleanStyleClassName = getBooleanStyleClassName(type, selfStyleMap);

            _className = `${_className} ${booleanStyleClassName}`;
        }

        const backgroundImageURL = (
            selfStyleMap?.[FEATURE_FLAG_KEYS.BACKGROUND_TYPE] === 'image'
                ? selfStyleMap?.[FEATURE_FLAG_KEYS.IMAGE_URL]
                : ''
        ) as string;

        const defaultBackgroundImageURL =
            selfStyleMap?.[FEATURE_FLAG_KEYS.BACKGROUND_TYPE] === 'image' &&
            selfStyleMap?.[FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]
                ? getS3ObjectUrl(
                      selfStyleMap?.[FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]
                  )
                : '';

        const backgroundVideoURL = (
            selfStyleMap?.[FEATURE_FLAG_KEYS.BACKGROUND_TYPE] === 'video'
                ? selfStyleMap?.[FEATURE_FLAG_KEYS.VIDEO_URL]
                : ''
        ) as string;

        const backgroundMaskColor =
            selfStyleMap?.[FEATURE_FLAG_KEYS.BACKGROUND_MASK_COLOR];

        let mediaSource = '';

        const defaultMediaSource =
            selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'image' &&
            selfStyleMap?.[FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]
                ? getS3ObjectUrl(
                      selfStyleMap?.[FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]
                  )
                : '';

        switch (true) {
            case selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'image':
                mediaSource = selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_IMAGE_URL] || '';

                break;

            case selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'video':
                mediaSource = selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_VIDEO_URL] || '';

                break;

            case selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'externalImage':
                mediaSource =
                    selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_IMAGE_EXTERNAL_URL] || '';

                break;

            case selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_TYPE] === 'externalVideo':
                mediaSource =
                    selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_VIDEO_EXTERNAL_URL] || '';

                break;
        }

        if (getResizedStyleMap && selfFeature.includes(FEATURE_FLAG_KEYS.RESIZE_HANDLE)) {
            Object.assign(style, getResizedStyleMap(id));
        }

        if (
            getCellResizedStyleMap &&
            (type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL ||
                type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL)
        ) {
            Object.assign(style, getCellResizedStyleMap(id));
        }

        const resizeHandleStyleList = [
            CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_LEFT],
            CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_RIGHT],
            CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_TOP],
            CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_BOTTOM],
        ];

        const containerStyle = {
            ...pick(style, [
                ...resizeHandleStyleList,
                CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_X],
                CSSVariablesMap[FEATURE_FLAG_KEYS.PADDING_Y],
            ]),
            ...(getResizedStyleMap ? getResizedStyleMap(id) : {}),
        };

        const enhancedContainerStyle = getDynamicSpacingStyleMap(containerStyle, type);

        const backgroundKey = CSSVariablesMap[FEATURE_FLAG_KEYS.BACKGROUND_COLOR];

        const omitStyle = omit(style, [
            ...resizeHandleStyleList,
            FEATURE_FLAG_KEYS.ANIMATION,
        ]);

        const enhancedOmitStyle = getDynamicSpacingStyleMap(omitStyle);

        const icon = getIconConfig(selfStyleMap);

        const mediaMaskColor =
            (selfStyleMap?.[FEATURE_FLAG_KEYS.ENABLE_MEDIA_MASK] &&
                selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_MASK_COLOR]) ||
            '';

        const autoPlay = selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_AUTO_PLAY];

        const muted = selfStyleMap?.[FEATURE_FLAG_KEYS.MEDIA_MUTE];

        const isPrimaryGrid = type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL;

        const background = {
            [backgroundKey]: enhancedOmitStyle[backgroundKey],
        };

        const containerExtraProps = isPrimaryGrid && {
            backgroundImageURL,
            backgroundVideoURL,
            defaultBackgroundImageURL,
            backgroundMaskColor,
            style: {
                ...background,
                [CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_FIT]]:
                    style[CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_FIT]],
                [CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_POSITION]]:
                    style[CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_POSITION]],
                [CSSVariablesMap[FEATURE_FLAG_KEYS.BORDER_RADIUS]]:
                    style[CSSVariablesMap[FEATURE_FLAG_KEYS.BORDER_RADIUS]],
                '--shop-ui-custom-background-width':
                    style['--shop-ui-custom-background-width'],
                '--shop-ui-custom-background-height':
                    style['--shop-ui-custom-background-height'],
            },
        };

        const motionSettings = (selfStyleMap?.[FEATURE_FLAG_KEYS.ANIMATION] || {}) as {
            motionStaggeringChildren: boolean;
            effects: `${MotionEffectType}-${MotionDuration}-${MotionRange}`[];
            gesture: MotionGesture;
        };

        return {
            id,
            className: _className,
            content: selfContent,
            type,
            variation,
            style: enhancedOmitStyle,
            styleMap: selfStyleMap,
            ...(!isPrimaryGrid && {
                backgroundImageURL,
                backgroundVideoURL,
                defaultBackgroundImageURL,
                backgroundMaskColor,
            }),
            mediaSource,
            defaultMediaSource,
            mediaMaskColor,
            containerStyle: enhancedContainerStyle,
            containerExtraProps,
            icon,
            autoPlay,
            muted,
            motionSettings,
        };
    }, [
        selfStyleMap,
        selfContent,
        selfFeature,
        type,
        id,
        getResizedStyleMap,
        getCellResizedStyleMap,
    ]);

    useEffectOnce(() => {
        uiStyleUpdater.subscribeStyleMapUpdate(id, enhanceSetStyleMap);
        uiStyleUpdater.subscribeContentMapUpdate(id, setSelfContent);
    });

    return property;
}

export type UsePreviewUIStyleReturn = ReturnType<typeof usePreviewUIStyle>;
